import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { colors, gradients, radii, shadows } from '../theme/designTokens';
import { createAlegionContact, normalizePhone, getContactSubmitErrorMessage } from '../services/contactService';
import FeedbackModal from './FeedbackModal';
import TurnstileField from './TurnstileField';
import HoneypotField from './HoneypotField';

const fieldSx = {
  mb: 2,
  '& .MuiOutlinedInput-root': {
    borderRadius: radii.sm,
    background: colors.white,
    '& fieldset': { borderColor: colors.border },
    '&:hover fieldset': { borderColor: colors.navy },
    '&.Mui-focused fieldset': { borderColor: colors.navy, borderWidth: 1 },
  },
  '& .MuiInputLabel-root.Mui-focused': { color: colors.navy },
};

const emptyForm = { name: '', phone: '', email_id: '', website: '' };

const AccessRequestModal = ({ open, onClose, topic = 'Access' }) => {
  const [formData, setFormData] = useState(emptyForm);
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileResetKey, setTurnstileResetKey] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [feedback, setFeedback] = useState({
    open: false,
    type: 'success',
    title: '',
    message: '',
  });

  const resetBotFields = () => {
    setTurnstileToken('');
    setTurnstileResetKey((k) => k + 1);
  };

  const handleClose = () => {
    if (isSubmitting) return;
    setFormData(emptyForm);
    setError('');
    resetBotFields();
    onClose();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'phone' ? value.replace(/\D/g, '').slice(0, 10) : value,
    }));
  };

  const validate = () => {
    if (!formData.name.trim()) return 'Name is required.';
    if (!formData.phone.trim()) return 'Mobile number is required.';
    if (normalizePhone(formData.phone).length !== 10) {
      return 'Please enter a valid 10-digit mobile number.';
    }
    const email = formData.email_id.trim();
    if (!email) return 'Email is required.';
    if (!/\S+@\S+\.\S+/.test(email)) return 'Please enter a valid email address.';
    if (!turnstileToken) return 'Please complete the CAPTCHA verification.';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const errorMsg = validate();
    if (errorMsg) {
      setError(errorMsg);
      return;
    }

    setIsSubmitting(true);
    try {
      await createAlegionContact({
        ...formData,
        message: `Access request: ${topic}`,
        turnstile_token: turnstileToken,
      });
      setFormData(emptyForm);
      resetBotFields();
      onClose();
      setFeedback({
        open: true,
        type: 'success',
        title: 'Request received!',
        message: `Thanks for requesting access to ${topic}. Our team will reach out shortly.`,
      });
    } catch (err) {
      console.error('Access request failed:', err);
      const message = getContactSubmitErrorMessage(err);
      setError(message);
      resetBotFields();
      setFeedback({
        open: true,
        type: 'error',
        title: 'Submission failed',
        message,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: radii.xl,
            overflow: 'hidden',
            boxShadow: shadows.lg,
          },
        }}
      >
        <Box
          sx={{
            background: gradients.navy,
            px: 3,
            py: 2.5,
            position: 'relative',
          }}
        >
          <IconButton
            onClick={handleClose}
            aria-label="Close"
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              color: 'rgba(255,255,255,0.7)',
              '&:hover': { color: colors.white, background: 'rgba(255,255,255,0.1)' },
            }}
          >
            <CloseIcon />
          </IconButton>
          <Typography sx={{ color: colors.ochreLight, fontSize: '0.7rem', letterSpacing: '0.14em', textTransform: 'uppercase', mb: 0.75 }}>
            Request access
          </Typography>
          <Typography sx={{ color: colors.white, fontWeight: 700, fontSize: '1.2rem', pr: 4 }}>
            {topic}
          </Typography>
        </Box>

        <DialogContent sx={{ px: 3, py: 3 }}>
          <Typography sx={{ color: colors.slate, fontSize: '0.9rem', lineHeight: 1.7, mb: 2.5 }}>
            Share your details and we will get back to you with access information.
          </Typography>

          <Box component="form" onSubmit={handleSubmit} noValidate>
            <TextField
              fullWidth
              required
              label="Name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              sx={fieldSx}
            />
            <TextField
              fullWidth
              required
              label="Mobile Number"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              inputProps={{ inputMode: 'numeric', maxLength: 10 }}
              sx={fieldSx}
            />
            <TextField
              fullWidth
              required
              label="Email"
              name="email_id"
              type="email"
              value={formData.email_id}
              onChange={handleChange}
              sx={fieldSx}
            />

            <HoneypotField value={formData.website} onChange={handleChange} />

            {open && (
              <TurnstileField
                resetKey={turnstileResetKey}
                onToken={setTurnstileToken}
                onExpire={() => setTurnstileToken('')}
              />
            )}

            {error && (
              <Typography sx={{ color: '#b91c1c', fontSize: '0.85rem', mb: 2 }}>
                {error}
              </Typography>
            )}

            <Button
              type="submit"
              fullWidth
              disabled={isSubmitting || !turnstileToken}
              sx={{
                py: 1.25,
                background: gradients.gold,
                color: colors.navy,
                fontWeight: 700,
                borderRadius: radii.pill,
                textTransform: 'none',
                fontSize: '1rem',
                boxShadow: shadows.gold,
                '&:hover': { background: colors.goldLight },
                '&.Mui-disabled': { opacity: 0.7, color: colors.navy },
              }}
            >
              {isSubmitting ? <CircularProgress size={22} sx={{ color: colors.navy }} /> : 'Submit request'}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      <FeedbackModal
        open={feedback.open}
        onClose={() => setFeedback((prev) => ({ ...prev, open: false }))}
        type={feedback.type}
        title={feedback.title}
        message={feedback.message}
      />
    </>
  );
};

export default AccessRequestModal;
